import React from 'react';
import { Heart, Users, Target, Package, Droplets, Flame, GlassWater, GraduationCap, Home as HomeIcon, ShieldCheck } from 'lucide-react';

const COUNTRY_FLAGS = {
  'Haiti': { emoji: '\u{1F1ED}\u{1F1F9}', color: 'bg-blue-900' },
  'Colombia': { emoji: '\u{1F1E8}\u{1F1F4}', color: 'bg-yellow-500' },
  'El Salvador': { emoji: '\u{1F1F8}\u{1F1FB}', color: 'bg-blue-600' },
  'Ecuador': { emoji: '\u{1F1EA}\u{1F1E8}', color: 'bg-yellow-400' },
  'Jamaica': { emoji: '\u{1F1EF}\u{1F1F2}', color: 'bg-green-700' },
  'Dominican Republic': { emoji: '\u{1F1E9}\u{1F1F4}', color: 'bg-red-700' },
  'USA': { emoji: '\u{1F1FA}\u{1F1F8}', color: 'bg-blue-800' },
};

export { COUNTRY_FLAGS };

export const ImpactTracker = ({ program, familiesFromDonations, progressPercentage, candleCount, costPerFamily }) => (
  <section className="bg-[#0A0A0A] py-8" data-testid="impact-tracker">
    <div className="px-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="text-center p-4 bg-white/5 rounded-lg">
          <Users className="w-5 h-5 text-[#BA1826] mx-auto mb-2" />
          <div className="text-2xl md:text-3xl font-bold text-white mb-1">{familiesFromDonations}</div>
          <span className="text-gray-400 text-xs">Families Supported</span>
        </div>
        <div className="text-center p-4 bg-white/5 rounded-lg">
          <Target className="w-5 h-5 text-[#BA1826] mx-auto mb-2" />
          <div className="text-2xl md:text-3xl font-bold text-white mb-1">{program.families_target}</div>
          <span className="text-gray-400 text-xs">Target Families</span>
        </div>
        <div className="text-center p-4 bg-white/5 rounded-lg">
          <Heart className="w-5 h-5 text-[#BA1826] mx-auto mb-2" />
          <div className="text-2xl md:text-3xl font-bold text-white mb-1">${program.amount_raised.toLocaleString()}</div>
          <span className="text-gray-400 text-xs">Amount Raised</span>
        </div>
        <div className="text-center p-4 bg-white/5 rounded-lg">
          <Target className="w-5 h-5 text-[#BA1826] mx-auto mb-2" />
          <div className="text-2xl md:text-3xl font-bold text-white mb-1">${program.monthly_goal.toLocaleString()}</div>
          <span className="text-gray-400 text-xs">Monthly Goal</span>
        </div>
        <div className="text-center p-4 bg-white/5 rounded-lg col-span-2 md:col-span-1">
          <Flame className="w-5 h-5 text-amber-400 mx-auto mb-2" />
          <div className="text-2xl md:text-3xl font-bold text-amber-400 mb-1">{candleCount}</div>
          <span className="text-gray-400 text-xs">Candles Lit</span>
        </div>
      </div>
      <div className="max-w-xl mx-auto">
        <div className="relative h-5 bg-white/10 rounded-full overflow-hidden mb-2">
          <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#BA1826] to-[#E53935] rounded-full transition-all duration-700" style={{ width: `${progressPercentage}%` }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-semibold text-white">{progressPercentage.toFixed(0)}%</span>
          </div>
        </div>
        <p className="text-center text-xs text-gray-500">
          Help us reach {program.families_target} families this month — every ${parseFloat(costPerFamily ?? program.cost_per_family ?? 0).toFixed(2)} changes a household.
        </p>
      </div>
    </div>
  </section>
);

export const ProgramOverview = ({ program }) => (
  <section className="py-10 bg-white" data-testid="program-overview">
    <div className="px-6">
      <div className="grid md:grid-cols-2 gap-8 items-start">
        <div>
          <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-3">About This Program</p>
          <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-4">School-Based Family Stabilization</h2>
          <p className="text-[#4B5563] leading-relaxed mb-4 text-sm">{program.overview_copy}</p>
          <div className="space-y-3 mt-6">
            {[
              { icon: GraduationCap, title: 'School-Based Model', desc: 'Schools identify families in need, ensuring fair and transparent distribution.' },
              { icon: HomeIcon, title: 'Full Household Support', desc: 'Supporting not just the student, but the entire family behind them.' },
              { icon: ShieldCheck, title: 'Dignity & Transparency', desc: 'Every dollar is tracked and reported so donors see exactly where support goes.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-9 h-9 bg-[#FEE2E2] rounded flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#BA1826]" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm">{title}</h4>
                  <p className="text-xs text-[#4B5563]">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="w-full aspect-[4/3] bg-[#F8F9FA] border-2 border-dashed border-[#E2E8F0] rounded-sm flex flex-col items-center justify-center p-8" data-testid="country-overview-image-placeholder">
            <span className="text-5xl mb-3" role="img" aria-label={`${program.country_name} flag`}>
              {COUNTRY_FLAGS[program.country_name]?.emoji || '\u{1F30D}'}
            </span>
            <p className="text-sm font-medium text-[#94A3B8] text-center">Add country program photo</p>
            <p className="text-xs text-[#CBD5E1] text-center mt-1">Use supplies, distribution setup, school/community environment, or non-identifiable impact photo</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const ThreePillarSystem = ({ program, foodItems, hygieneItems, foodCost, hygieneCost, waterCost, foodSize, hygieneSize, fullFamilyCost, sectionsEnabled }) => {
  const showFood = sectionsEnabled?.food_bucket !== false;
  const showHygiene = sectionsEnabled?.hygiene_kit !== false;
  const showWater = sectionsEnabled?.water !== false;
  return (
  <section className="py-10 bg-[#F8F9FA]" data-testid="bucket-system">
    <div className="px-6">
      <div className="text-center mb-8">
        <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-2">What Each Family Receives</p>
        <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-2">Three-Pillar Support System</h2>
        <p className="text-sm text-[#4B5563] max-w-lg mx-auto">Each family receives food essentials, hygiene products, and clean water access — three core needs addressed with one contribution.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-5 max-w-3xl mx-auto">
        {showFood && (
          <BucketCard title="Food Essentials" icon={Package} bgColor="bg-[#BA1826]" items={foodItems} subtitle={`${foodSize} — $${foodCost}`} testId="food-bucket" description={program.food_bucket_description} />
        )}
        {showHygiene && (
          <BucketCard title="Hygiene Essentials" icon={Droplets} bgColor="bg-[#0A0A0A]" items={hygieneItems} subtitle={`${hygieneSize} — $${hygieneCost}`} testId="hygiene-bucket" description={program.hygiene_kit_description} />
        )}
        {showWater && (
        <div className="bg-white border-2 border-[#BAE6FD] rounded-lg overflow-hidden" data-testid="water-bucket">
          <div className="bg-[#1E6B8A] p-4 text-center">
            <GlassWater className="w-6 h-6 text-white mx-auto mb-1" />
            <h3 className="font-heading text-lg font-bold text-white">Clean Water System</h3>
            <p className="text-white/80 text-xs">${waterCost}</p>
          </div>
          <div className="p-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between py-1.5 border-b border-[#F1F5F9]">
                <span className="text-xs text-[#4B5563]">LifeStraw Family Filter</span>
                <span className="text-xs font-medium text-[#0A0A0A] bg-[#F0F9FF] px-2 py-0.5 rounded">1 unit</span>
              </div>
            </div>
            <p className="text-xs text-[#4B5563] mt-3 leading-relaxed">Provides safe drinking water by removing bacteria, parasites, and viruses.</p>
          </div>
        </div>
        )}
      </div>
      <div className="max-w-xl mx-auto mt-8" data-testid="impact-diagram">
        <div className="bg-white border-2 border-[#BA1826] rounded-lg overflow-hidden">
          <div className="bg-[#FEF2F2] border-b border-[#FECACA] p-4 text-center">
            <p className="text-xs uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-1">Full Family Support System</p>
            <div className="inline-flex items-baseline gap-2">
              <span className="text-3xl font-bold text-[#BA1826]">${fullFamilyCost}</span>
              <span className="text-sm text-[#4B5563]">=</span>
              <span className="text-sm font-semibold text-[#0A0A0A]">1 Family Supported</span>
            </div>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-3 bg-[#FEF2F2] rounded-lg">
                <Package className="w-4 h-4 text-[#BA1826] mx-auto mb-1" />
                <p className="text-[10px] font-semibold text-[#0A0A0A] mb-0.5">Food</p>
                <p className="text-base font-bold text-[#BA1826]">${foodCost}</p>
              </div>
              <div className="text-center p-3 bg-[#F8F9FA] rounded-lg">
                <Droplets className="w-4 h-4 text-[#0A0A0A] mx-auto mb-1" />
                <p className="text-[10px] font-semibold text-[#0A0A0A] mb-0.5">Hygiene</p>
                <p className="text-base font-bold text-[#0A0A0A]">${hygieneCost}</p>
              </div>
              <div className="text-center p-3 bg-[#F0F9FF] rounded-lg">
                <GlassWater className="w-4 h-4 text-[#1E6B8A] mx-auto mb-1" />
                <p className="text-[10px] font-semibold text-[#0A0A0A] mb-0.5">Water</p>
                <p className="text-base font-bold text-[#1E6B8A]">${waterCost}</p>
              </div>
            </div>
            <p className="text-xs text-[#4B5563] text-center leading-relaxed">Each contribution provides food, hygiene essentials, and safe drinking water for one family.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

const BucketCard = ({ title, icon: Icon, bgColor, items, subtitle, testId, description }) => (
  <div className="bg-white border-2 border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={testId}>
    <div className={`${bgColor} p-4 text-center`}>
      <Icon className="w-6 h-6 text-white mx-auto mb-1" />
      <h3 className="font-heading text-lg font-bold text-white">{title}</h3>
      <p className="text-white/80 text-xs">{subtitle}</p>
    </div>
    <div className="p-4">
      {description && (
        <p className="text-xs text-[#4B5563] mb-3 leading-relaxed italic">{description}</p>
      )}
      <div className="space-y-1.5">
        {items.map((item) => (
          <div key={item.item} className="flex items-center justify-between py-1.5 border-b border-[#F1F5F9] last:border-0">
            <span className="text-xs text-[#4B5563]">{item.item}</span>
            <span className="text-xs font-medium text-[#0A0A0A] bg-[#F8F9FA] px-2 py-0.5 rounded">{item.quantity}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const ScalingSection = ({ program }) => (
  <section className="py-8 bg-white" data-testid="scaling-section">
    <div className="px-6">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-5">
          <p className="text-xs uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-1">How Your Support Scales</p>
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Every Dollar Multiplies Impact</h3>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-[#0A0A0A] mb-0.5">10</div>
            <p className="text-[10px] text-[#4B5563] mb-2">Families</p>
            <p className="text-sm font-bold text-[#BA1826]">$700</p>
          </div>
          <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-[#0A0A0A] mb-0.5">25</div>
            <p className="text-[10px] text-[#4B5563] mb-2">Families</p>
            <p className="text-sm font-bold text-[#BA1826]">$1,750</p>
          </div>
          <div className="bg-[#FEF2F2] border border-[#BA1826]/20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-[#BA1826] mb-0.5">{program.families_target}</div>
            <p className="text-[10px] text-[#4B5563] mb-2">Families (Goal)</p>
            <p className="text-sm font-bold text-[#BA1826]">${program.monthly_goal.toLocaleString()}</p>
          </div>
        </div>
        <p className="text-xs text-[#4B5563] text-center leading-relaxed">This includes sourcing, packaging, transportation, and distribution costs to ensure consistent delivery.</p>
      </div>
    </div>
  </section>
);

export const WaterSection = ({ program, waterCost, waterProcessing, handleWaterDonate }) => {
  if (!program.water_section_visible) return null;
  return (
    <section className="py-10 bg-white" data-testid="water-section">
      <div className="px-6">
        <div className="text-center mb-8">
          <p className="text-sm uppercase tracking-[0.15em] text-[#1E6B8A] font-semibold mb-2">Core Support Area</p>
          <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-2">Clean Water Access</h2>
          <p className="text-sm text-[#4B5563] max-w-lg mx-auto">
            {program.water_description || 'Providing safe drinking water solutions for families through long-lasting filtration systems.'}
          </p>
        </div>
        <div className="max-w-2xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-center justify-center">
              {program.water_image ? (
                <img src={program.water_image} alt="Clean water system" className="w-full h-auto rounded-lg" />
              ) : (
                <div className="w-full aspect-[4/3] bg-[#F0F9FF] border-2 border-dashed border-[#BAE6FD] rounded-lg flex flex-col items-center justify-center p-8" data-testid="water-image-placeholder">
                  <GlassWater className="w-8 h-8 text-[#7DD3FC] mb-3" />
                  <p className="text-sm font-medium text-[#64748B] text-center">Add clean water system image</p>
                  <p className="text-xs text-[#94A3B8] text-center mt-1">Filtration diagram, or approved field photo here</p>
                </div>
              )}
            </div>
            <div>
              <div className="space-y-3 mb-5">
                {[1, 2].map((qty) => (
                  <div key={qty} className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-lg p-4" data-testid={`water-tier-${qty}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <GlassWater className="w-4 h-4 text-[#1E6B8A]" />
                        <span className="text-sm font-semibold text-[#0A0A0A]">{qty} Water System{qty > 1 ? 's' : ''}</span>
                      </div>
                      <span className="text-lg font-bold text-[#1E6B8A]">${waterCost * qty}</span>
                    </div>
                    <p className="text-xs text-[#4B5563] mb-3">
                      {qty === 1 ? 'Supports 1 household with safe, filtered drinking water through a long-lasting filtration system.' : 'Supports up to 2 households or extends clean water access for a larger family unit.'}
                    </p>
                    <button onClick={() => handleWaterDonate(qty)} disabled={waterProcessing}
                      className={`w-full ${qty === 1 ? 'bg-[#1E6B8A]' : 'bg-[#1E6B8A]/80'} text-white text-xs font-semibold py-2 px-4 rounded hover:bg-[#185a74] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5`}
                      data-testid={`donate-water-${qty}-btn`}>
                      <GlassWater className="w-3.5 h-3.5" />
                      {waterProcessing ? 'Processing...' : `Donate ${qty} Water System${qty > 1 ? 's' : ''}`}
                    </button>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2" data-testid="water-impact-stats">
                <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-[#0A0A0A]">{program.water_systems_distributed || 0}</div>
                  <span className="text-[10px] text-[#4B5563]">Systems Distributed</span>
                </div>
                <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-[#0A0A0A]">{program.water_households_supported || 0}</div>
                  <span className="text-[10px] text-[#4B5563]">Households Supported</span>
                </div>
                <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-[#0A0A0A]">{(program.water_liters_provided || 0).toLocaleString()}</div>
                  <span className="text-[10px] text-[#4B5563]">Liters Provided</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
